import { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw, Activity } from "lucide-react";

const statusColors = {
  ok:   { bg: "rgba(34,197,94,.10)",  border: "rgba(34,197,94,.35)",  text: "#22c55e", label: "OK" },
  warn: { bg: "rgba(234,179,8,.10)",  border: "rgba(234,179,8,.35)",  text: "#eab308", label: "SLOW" },
  fail: { bg: "rgba(239,68,68,.10)",  border: "rgba(239,68,68,.35)",  text: "#ef4444", label: "FAIL" },
};

function StatusChip({ status }) {
  const c = statusColors[status] || statusColors.warn;
  const Icon = status === "ok" ? CheckCircle2 : status === "warn" ? AlertTriangle : XCircle;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold"
      style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text }}
      data-testid={`diag-status-${status}`}
    >
      <Icon size={11} /> {c.label}
    </span>
  );
}

export default function AdminDiagnostics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pingLatency, setPingLatency] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    // Fire the un-auth liveness ping in parallel — measures true edge latency.
    const t0 = performance.now();
    api.get("/diagnostics/ping").then(() => setPingLatency(Math.round(performance.now() - t0))).catch(() => {});
    try {
      const r = await api.get("/diagnostics/system");
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || { ok: 0, warn: 0, fail: 0, total: 0 };
  const overallStatus = summary.fail > 0 ? "fail" : summary.warn > 0 ? "warn" : "ok";
  const overallColor = statusColors[overallStatus];

  return (
    <AdminLayout>
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="fl-label text-[#f47a20]">System diagnostics</div>
          <h1 className="fl-heading text-4xl mt-2">
            {overallStatus === "ok"   && "All systems operational"}
            {overallStatus === "warn" && "Some services degraded"}
            {overallStatus === "fail" && "Attention needed"}
          </h1>
          <p className="text-sm mt-2" style={{ color: "var(--fl-muted)" }}>
            Live health of every subsystem the platform depends on. Refreshes on demand.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="diag-refresh-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 hover:translate-y-[-1px] transition-all"
          style={{
            background: overallColor.bg,
            border: `1px solid ${overallColor.border}`,
            color: overallColor.text,
          }}
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          {loading ? "Checking…" : "Re-run checks"}
        </button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <SummaryTile label="Healthy" value={summary.ok} tone="ok" />
        <SummaryTile label="Degraded" value={summary.warn} tone="warn" />
        <SummaryTile label="Failing" value={summary.fail} tone="fail" />
        <SummaryTile
          label="Edge latency"
          value={pingLatency !== null ? `${pingLatency}ms` : "—"}
          tone={pingLatency === null ? "warn" : pingLatency < 400 ? "ok" : pingLatency < 900 ? "warn" : "fail"}
          icon={<Activity size={14} />}
        />
      </div>

      {/* Checks list */}
      <div className="fl-card overflow-hidden" data-testid="diag-checks-list">
        <div className="grid grid-cols-12 gap-4 p-3 border-b border-white/10 fl-label">
          <div className="col-span-4">Subsystem</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Latency</div>
          <div className="col-span-4">Detail</div>
        </div>
        {(data?.checks || []).length === 0 && !loading && (
          <div className="p-6 text-sm" style={{ color: "var(--fl-muted-2)" }}>
            No checks yet — hit “Re-run checks”.
          </div>
        )}
        {(data?.checks || []).map((c) => (
          <div key={c.name} className="grid grid-cols-12 gap-4 p-3 border-b border-white/5 items-center"
               data-testid={`diag-row-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="col-span-4 text-sm">{c.name}</div>
            <div className="col-span-2"><StatusChip status={c.status} /></div>
            <div className="col-span-2 text-xs" style={{ color: "var(--fl-muted)" }}>{c.latency_ms} ms</div>
            <div className="col-span-4 text-xs" style={{ color: "var(--fl-muted)" }}>{c.detail}</div>
          </div>
        ))}
      </div>

      {data?.at && (
        <div className="mt-4 text-[11px]" style={{ color: "var(--fl-muted-2)" }} data-testid="diag-last-run">
          Last checked: {new Date(data.at).toLocaleString("en-GB")}
        </div>
      )}
    </AdminLayout>
  );
}

function SummaryTile({ label, value, tone, icon }) {
  const c = statusColors[tone] || statusColors.ok;
  return (
    <div
      className="p-4 rounded-md"
      style={{ background: c.bg, border: `1px solid ${c.border}` }}
      data-testid={`diag-summary-${tone}`}
    >
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold" style={{ color: c.text }}>
        {icon} {label}
      </div>
      <div className="text-3xl font-bold mt-2" style={{ color: "var(--fl-text)" }}>{value}</div>
    </div>
  );
}
